/**
 * USB Print Utility for Star Printers via Android Intent
 * 
 * This utility generates print-ready content that can be sent to
 * Star printers connected via USB to Android tablets using the Star Print app.
 * 
 * Architecture:
 * - KDS tablets connect to BC-Namaka-S WiFi
 * - Orders flow: POS → Backend → KDS tablets (via WiFi)
 * - Printers connect via USB-C to each tablet
 * - Print command uses Star Print app's intent system
 */

// Generate a printable ticket for a station
export const generateStationTicket = (order, station) => {
  const { order_number, customer_name, order_type, items, special_instructions, created_at } = order;
  
  // Filter items for this station
  const stationItems = items?.filter(item => item.station === station) || [];
  
  if (stationItems.length === 0) return null;
  
  const time = new Date(created_at).toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
  
  // Build ticket content
  let ticket = '';
  ticket += '================================\n';
  ticket += `       ${station.toUpperCase()} STATION\n`;
  ticket += '================================\n';
  ticket += `Order #${order_number || order.order_id?.slice(0, 8).toUpperCase()}\n`;
  ticket += `Time: ${time}\n`;
  ticket += `Type: ${order_type?.toUpperCase() || 'TAKEAWAY'}\n`;
  if (customer_name && customer_name !== 'Guest') {
    ticket += `Customer: ${customer_name}\n`;
  }
  ticket += '--------------------------------\n';
  
  // Items
  stationItems.forEach(item => {
    const qty = item.quantity || 1;
    ticket += `${qty}x ${item.name}\n`;
    if (item.size) {
      ticket += `   Size: ${item.size}\n`;
    }
    if (item.customizations?.length > 0) {
      item.customizations.forEach(c => {
        const customText = typeof c === 'string' ? c : c.name || '';
        if (customText) {
          ticket += `   • ${customText}\n`;
        }
      });
    }
    if (item.special_instructions) {
      ticket += `   ⚠ ${item.special_instructions}\n`;
    }
  });
  
  ticket += '--------------------------------\n';
  
  if (special_instructions) {
    ticket += `NOTE: ${special_instructions}\n`;
    ticket += '--------------------------------\n';
  }
  
  ticket += `Items: ${stationItems.reduce((sum, i) => sum + (i.quantity || 1), 0)}\n`;
  ticket += '================================\n\n\n';
  
  return ticket;
};

// Generate customer receipt
export const generateCustomerReceipt = (order, settings = {}) => {
  const { 
    order_number, 
    customer_name, 
    order_type, 
    items, 
    total, 
    payment_method,
    created_at,
    special_instructions 
  } = order;
  
  const time = new Date(created_at).toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
  const date = new Date(created_at).toLocaleDateString('en-US');
  
  let receipt = '';
  receipt += '================================\n';
  receipt += '       BULACCINO CAFE\n';
  receipt += '      Namaka, Nadi, Fiji\n';
  receipt += '================================\n';
  receipt += `Date: ${date}  Time: ${time}\n`;
  receipt += `Order #${order_number || order.order_id?.slice(0, 8).toUpperCase()}\n`;
  receipt += `Type: ${order_type?.toUpperCase() || 'TAKEAWAY'}\n`;
  if (customer_name && customer_name !== 'Guest') {
    receipt += `Customer: ${customer_name}\n`;
  }
  receipt += '--------------------------------\n';
  
  // Items
  let subtotal = 0;
  items?.forEach(item => {
    const qty = item.quantity || 1;
    const price = item.price || 0;
    const lineTotal = qty * price;
    subtotal += lineTotal;
    
    receipt += `${qty}x ${item.name}\n`;
    receipt += `   $${lineTotal.toFixed(2)}\n`;
    if (item.size) {
      receipt += `   (${item.size})\n`;
    }
    if (item.customizations?.length > 0) {
      item.customizations.forEach(c => {
        const customText = typeof c === 'string' ? c : c.name || '';
        if (customText) {
          receipt += `   + ${customText}\n`;
        }
      });
    }
  });
  
  receipt += '--------------------------------\n';
  
  // Totals
  const vatRate = settings.vat_rate || 15;
  const vatAmount = subtotal * (vatRate / 100);
  const finalTotal = total || subtotal;
  
  receipt += `Subtotal:     $${subtotal.toFixed(2)}\n`;
  receipt += `VAT (${vatRate}%):    $${vatAmount.toFixed(2)}\n`;
  receipt += `--------------------------------\n`;
  receipt += `TOTAL:        $${finalTotal.toFixed(2)}\n`;
  receipt += `--------------------------------\n`;
  receipt += `Paid by: ${payment_method?.toUpperCase() || 'CASH'}\n`;
  
  if (special_instructions) {
    receipt += `\nNote: ${special_instructions}\n`;
  }
  
  receipt += '\n================================\n';
  receipt += '    Vinaka - Thank You!\n';
  receipt += '   bulaccinocafe.com\n';
  receipt += '================================\n\n\n';
  
  return receipt;
};

// Trigger print via Star Print app intent (Android)
export const triggerUSBPrint = async (content, printerType = 'receipt') => {
  // Check if we're on Android
  const isAndroid = /android/i.test(navigator.userAgent);
  
  if (!isAndroid) {
    // Fallback: Open print dialog for desktop browsers
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Ticket</title>
            <style>
              body { 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                white-space: pre-wrap;
                padding: 10px;
              }
            </style>
          </head>
          <body>${content}</body>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </html>
      `);
      printWindow.document.close();
    }
    return { success: true, method: 'browser-print' };
  }
  
  // Android: Use Star Print app intent
  // The Star Print app registers a custom URL scheme
  try {
    // Method 1: Try Star Print app intent
    const encodedContent = encodeURIComponent(content);
    
    // Star CloudPRNT uses a different approach - we'll use a data URL
    // that the Star Print app can intercept
    const starPrintUrl = `starprint://print?data=${encodedContent}`;
    
    // Try to open Star Print app
    const intentOpened = window.open(starPrintUrl, '_blank');
    
    if (!intentOpened) {
      // Fallback: Use Web Share API or download as text file
      if (navigator.share) {
        await navigator.share({
          title: 'Print Ticket',
          text: content
        });
        return { success: true, method: 'share' };
      } else {
        // Download as text file that can be printed
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ticket-${Date.now()}.txt`;
        a.click();
        URL.revokeObjectURL(url);
        return { success: true, method: 'download' };
      }
    }
    
    return { success: true, method: 'star-print-intent' };
  } catch (error) {
    console.error('USB Print error:', error);
    return { success: false, error: error.message };
  }
};

// Print order items for a specific station
export const printStationOrder = async (order, station) => {
  const ticket = generateStationTicket(order, station);
  if (!ticket) {
    return { success: false, error: 'No items for this station' };
  }
  
  return await triggerUSBPrint(ticket, 'ticket');
};

// Print customer receipt
export const printCustomerReceipt = async (order, settings) => {
  const receipt = generateCustomerReceipt(order, settings);
  return await triggerUSBPrint(receipt, 'receipt');
};

export default {
  generateStationTicket,
  generateCustomerReceipt,
  triggerUSBPrint,
  printStationOrder,
  printCustomerReceipt
};
